// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axiosConfig';
import { fmtTime } from '../utils/timezoneUtils';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ContentSkeleton from './ContentSkeleton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiIcon from '@mui/icons-material/Wifi';
import ComputerIcon from '@mui/icons-material/Computer';
import RouterIcon from '@mui/icons-material/Router';
import PhoneIcon from '@mui/icons-material/PhoneAndroid';
import PrintIcon from '@mui/icons-material/Print';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import { useTheme } from '../context/ThemeContext';

const hasVpnIp = (client) => !!(client?.vpn_ip && String(client.vpn_ip).trim());

/**
 * Classify a client into an icon category. Device fingerprinting is
 * probabilistic and can return a different guess on each poll, so this also
 * falls back to service hints (RDP/SMB => computer, IPP => printer, ...).
 */
const classifyDevice = (client) => {
  const type = (client?.device_info?.device_type || '').toLowerCase();
  if (type.includes('printer')) return 'printer';
  if (type.includes('phone') || type.includes('mobile') || type.includes('tablet'))
    return 'phone';
  if (
    type.includes('router') ||
    type.includes('gateway') ||
    type.includes('switch') ||
    type.includes('access point')
  )
    return 'router';
  if (
    type.includes('computer') ||
    type.includes('pc') ||
    type.includes('laptop') ||
    type.includes('windows') ||
    type.includes('mac') ||
    type.includes('linux') ||
    type.includes('workstation') ||
    type.includes('server')
  )
    return 'computer';

  // Fingerprint inconclusive — infer from advertised services.
  const services = (client?.device_info?.services || []).map((s) => String(s).toLowerCase());
  if (services.some((s) => ['rdp', 'smb', 'netbios', 'ssh', 'vnc'].includes(s)))
    return 'computer';
  if (services.some((s) => ['ipp', 'printer', 'jetdirect'].includes(s))) return 'printer';

  return 'generic';
};

const DEVICE_CLASS_ICONS = {
  printer: <PrintIcon />,
  phone: <PhoneIcon />,
  router: <RouterIcon />,
  computer: <ComputerIcon />,
  generic: <DevicesOtherIcon />,
};

/** Keep only eth1 clients that have a VPN (WG) address; recompute summary counts. */
const filterVpnClients = (data) => {
  if (!data || typeof data !== 'object') return data;
  const filterList = (list) => (Array.isArray(list) ? list.filter(hasVpnIp) : list);
  const all_clients = filterList(data.all_clients) || [];
  const static_clients = filterList(data.static_clients) || [];
  const dhcp_clients = filterList(data.dhcp_clients) || [];
  return {
    ...data,
    all_clients,
    static_clients,
    dhcp_clients,
    static_count: static_clients.length,
    dhcp_count: dhcp_clients.length,
    total_clients: all_clients.length,
  };
};

const StargateEthernetClients = ({ ip, apiKey, healthStatus }) => {
  const { isDarkMode } = useTheme();
  const [clientsData, setClientsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const intervalRef = useRef(null);
  // Interval callbacks close over stale state; track "data on screen" via ref.
  const hasDataRef = useRef(false);

  const fetchClients = async (showLoading = true) => {
    if (!ip || !apiKey) {
      setError('IP address or API key is missing');
      return;
    }

    if (showLoading) setLoading(true);

    try {
      const response = await axios.get('/proxy/network/ethernet-clients-metadata', {
        params: {
          ip: ip,
          api_key: apiKey,
          interface: 'eth1',
        },
        timeout: 15000,
      });

      if (response.data) {
        const next = filterVpnClients(response.data);
        // Skip no-op updates so the 30s poll doesn't re-render an unchanged table.
        setClientsData((prev) =>
          JSON.stringify(prev) === JSON.stringify(next) ? prev : next
        );
        hasDataRef.current = true;
        setLastRefresh(new Date());
        setError(null);
      }
    } catch (err) {
      console.error('[Ethernet Clients] Fetch error:', err);
      // Background poll failure with data already on screen: keep showing the
      // stale table instead of injecting an error Alert that shifts the layout.
      if (showLoading || !hasDataRef.current) {
        setError(err.response?.data?.detail || err.message || 'Failed to fetch Ethernet clients');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (healthStatus && healthStatus !== 'green') {
      setClientsData(null);
      hasDataRef.current = false;
      setError(null);
      setLoading(false);
      return;
    }

    fetchClients(true);

    // Refresh every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchClients(false);
    }, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [ip, apiKey, healthStatus]);

  // Sticky icon classification per device (keyed by MAC): once a device
  // resolves to a specific class, keep it even if a later fingerprint poll
  // comes back inconclusive. Prevents the icon flipping between refreshes.
  const iconClassByDeviceRef = useRef({});

  const getDeviceIcon = (client) => {
    const key = client?.mac_address || client?.ip_address || client?.hostname || '';
    let cls = classifyDevice(client);
    if (cls === 'generic' && key && iconClassByDeviceRef.current[key]) {
      cls = iconClassByDeviceRef.current[key];
    } else if (cls !== 'generic' && key) {
      iconClassByDeviceRef.current[key] = cls;
    }
    return DEVICE_CLASS_ICONS[cls];
  };

  const getStatusColor = (status) => {
    if (status === 'online') return 'success';
    if (status === 'offline') return 'default';
    return 'warning';
  };

  const formatServices = (services) => {
    if (!services || !Array.isArray(services) || services.length === 0) return 'None';
    return services.join(', ');
  };

  if (!ip || !apiKey) {
    return (
      <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent>
          <Alert severity="warning">IP address or API key is missing</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WifiIcon />
            <Typography variant="h6">Ethernet Clients (ETH1)</Typography>
            {clientsData && (
              <Chip
                label={`${clientsData.total_clients || 0} Total`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastRefresh && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                {fmtTime(lastRefresh)}
              </Typography>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchClients(true)} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <Divider />
      <CardContent>
        {loading && !clientsData && <ContentSkeleton variant="table" rows={4} columns={4} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {clientsData && (
          <Box>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Total Clients
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {clientsData.total_clients || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Static Clients
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {clientsData.static_count || 0}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    p: 2,
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.06)' : '#f5f7fa',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    DHCP Clients
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {clientsData.dhcp_count || 0}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Clients List */}
            {clientsData.all_clients && clientsData.all_clients.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  All Clients
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    background: isDarkMode ? 'rgba(138, 148, 168, 0.04)' : '#ffffff',
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Hostname</TableCell>
                        <TableCell>IP Address</TableCell>
                        <TableCell>MAC Address</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Device Type</TableCell>
                        <TableCell>VPN IP</TableCell>
                        <TableCell>Services</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientsData.all_clients.map((client, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {getDeviceIcon(client)}
                              <Typography variant="body2">
                                {client.hostname || 'Unknown'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {client.ip_address || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                              }}
                            >
                              {client.mac_address || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={client.client_type || 'unknown'}
                              size="small"
                              color={client.client_type === 'static' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={client.status || 'unknown'}
                              size="small"
                              color={getStatusColor(client.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {client.device_info?.device_type || 'Unknown'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {client.vpn_ip ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: 'monospace',
                                  color: '#81c784',
                                }}
                              >
                                {client.vpn_ip}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                              {formatServices(client.device_info?.services)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Alert severity="info">No eth1 clients with a VPN IP</Alert>
            )}
          </Box>
        )}

        {!loading && !clientsData && !error && <Alert severity="info">No data available</Alert>}
      </CardContent>
    </Card>
  );
};

export default StargateEthernetClients;
